import {
    type ChartModuleDefinition,
    type DeepPartial,
    Logger,
    ModuleRegistry,
    ModuleType,
    getDocument,
    getWindow,
    groupBy,
    isArray,
    isEnumValue,
    isFiniteNumber,
    isObject,
    isPlainObject,
    isString,
    isSymbol,
    joinFormatted,
    setDocument,
    setWindow,
    unique,
    validate,
} from 'ag-charts-core';
import {
    type AgCartesianAxisOptions,
    type AgChartOptions,
    type AgChartThemeParams,
    type AgPolarAxisOptions,
    type AgPresetOptions,
    type AgPresetOverrides,
    AgTooltipAnchorToType,
    AgTooltipPlacementType,
    type AgTooltipPositionOptions,
    AgTooltipPositionType, // eslint-disable-line sonarjs/deprecation
    type WithThemeParams,
} from 'ag-charts-types';

import { PRESETS, PRESET_DATA_PROCESSORS } from '../api/preset/presets';
import { removeUnusedEnterpriseOptions, removeUsedEnterpriseOptions } from '../chart/factory/processEnterpriseOptions';
import { seriesRegistry } from '../chart/factory/seriesRegistry';
import { getChartTheme, themeOptionsDef } from '../chart/mapping/themes';
import {
    type SeriesOptionsTypes,
    isAgCartesianChartOptions,
    isAgPolarChartOptionsWithSeriesBasedLegend,
    isAgStandaloneChartOptions,
} from '../chart/mapping/types';
import { type ChartTheme } from '../chart/themes/chartTheme';
import { Debug } from '../util/debug';
import {
    type CloneOptions,
    deepClone,
    jsonDiff,
    jsonPropertyCompare,
    jsonResolveOperations,
    jsonWalk,
} from '../util/json';
import { deepFreeze, merge, mergeArrayDefaults, mergeDefaults } from '../util/object';
import { paletteType } from './coreModulesTypes';
import { enterpriseModule } from './enterpriseModule';
import type { SeriesType } from './optionsModuleTypes';

export interface ChartSpecialOverrides {
    document: Document;
    window: Window;
    sceneMode?: 'simple';
    styleContainer?: HTMLElement;
}

export interface ChartInternalOptionMetadata {
    presetType?: keyof typeof PRESETS;
    pool?: boolean;
    domMode?: 'normal' | 'minimal';
}

type GroupingOptions = {
    grouped?: boolean;
    stacked?: boolean;
    stackGroup?: string;
    seriesGrouping?: {
        groupIndex: number;
        groupCount: number;
        stackIndex: number;
        stackCount: number;
    };
};
type GroupingSeriesOptions = SeriesOptionsTypes & GroupingOptions & { xKey?: string };
type SeriesGroup = { groupType: GroupingType; seriesType: string; series: GroupingSeriesOptions[]; groupId: string };

enum GroupingType {
    DEFAULT = 'default',
    STACK = 'stack',
    GROUP = 'group',
}

const stringFormat = (value: string) => `'${value}'`;

export class ChartOptions<T extends AgChartOptions = AgChartOptions> {
    public static readonly OPTIONS_CLONE_OPTS: CloneOptions = {
        shallow: new Set(['data', 'container']),
        assign: new Set(['context', 'theme']),
    };
    public static readonly JSON_DIFF_OPTS = new Set<any>(['data']);

    private static readonly perfDebug = Debug.create(true, 'perf');

    private static readonly FAST_PATH_OPTIONS = new Set<keyof AgChartOptions>(['data', 'width', 'height', 'container']);
    private static isFastPathDelta(deltaOptions: DeepPartial<AgChartOptions> | null) {
        for (const key of Object.keys(deltaOptions ?? {})) {
            if (!this.FAST_PATH_OPTIONS.has(key as keyof AgChartOptions)) {
                ChartOptions.perfDebug('ChartOptions.isFastPathDelta() - slow path required due to presence of: ', key);
                return false;
            }
        }
        ChartOptions.perfDebug(`ChartOptions.isFastPathDelta() - fast path possible.`);
        return true;
    }

    activeTheme: ChartTheme;
    processedOptions: T;
    defaultAxes: T;
    userOptions: Partial<T>;
    processedOverrides: Partial<T>;
    specialOverrides: ChartSpecialOverrides;
    optionMetadata: ChartInternalOptionMetadata;
    themeParameters: AgChartThemeParams = {};
    annotationThemes: any;
    fastDelta?: DeepPartial<T>;
    chartDef?: ChartModuleDefinition<any>;

    private static readonly debug = Debug.create(true, 'opts');

    constructor(
        currentUserOptions: T | ChartOptions<T> | undefined,
        newUserOptions: T,
        processedOverrides: Partial<T>,
        specialOverrides: Partial<ChartSpecialOverrides>,
        metadata: ChartInternalOptionMetadata,
        deltaOptions?: DeepPartial<T> | null,
        stripSymbols = false
    ) {
        this.optionMetadata = metadata ?? {};
        this.processedOverrides = processedOverrides ?? {};

        let baseChartOptions: ChartOptions<T> | null = null;
        if (currentUserOptions instanceof ChartOptions) {
            // Delta update case.
            baseChartOptions = currentUserOptions;
            this.specialOverrides = baseChartOptions.specialOverrides;

            if (deltaOptions === undefined) {
                // No diff case - null means diff was a no-op.
                deltaOptions = jsonDiff(
                    baseChartOptions.userOptions as T,
                    newUserOptions,
                    ChartOptions.JSON_DIFF_OPTS
                ) as DeepPartial<T>;
            }

            this.userOptions = deepClone(
                merge(deltaOptions, baseChartOptions.userOptions),
                ChartOptions.OPTIONS_CLONE_OPTS
            ) as T;
        } else {
            // Full update case.
            this.userOptions = deepClone(currentUserOptions ?? newUserOptions, ChartOptions.OPTIONS_CLONE_OPTS);
            this.specialOverrides = this.specialOverridesDefaults({ ...specialOverrides });
        }

        if (stripSymbols) {
            this.removeLeftoverSymbols(this.userOptions);
        }

        let activeTheme, processedOptions, defaultAxes, fastDelta, themeParameters, annotationThemes;
        if (
            !stripSymbols &&
            deltaOptions !== undefined &&
            ChartOptions.isFastPathDelta(deltaOptions) &&
            baseChartOptions != null
        ) {
            ({ activeTheme, processedOptions, defaultAxes, fastDelta } = this.fastSetup(
                deltaOptions,
                baseChartOptions
            ));
            themeParameters = baseChartOptions.themeParameters;
            annotationThemes = baseChartOptions.annotationThemes;
        } else {
            ChartOptions.perfDebug(`ChartOptions.slowSetup()`);
            ({ activeTheme, processedOptions, defaultAxes, themeParameters, annotationThemes } = this.slowSetup(
                processedOverrides,
                deltaOptions,
                stripSymbols
            ));
        }

        this.activeTheme = activeTheme;
        this.processedOptions = processedOptions;
        this.defaultAxes = defaultAxes;
        this.fastDelta = fastDelta ?? undefined;
        this.themeParameters = themeParameters;
        this.annotationThemes = annotationThemes;

        // This ChartOptions should be treated as immutable from here-on, force immutability to
        // flush out runtime issues.
        Debug.inDevelopmentMode(() => deepFreeze(this));
    }

    private fastSetup(deltaOptions: DeepPartial<T> | null, baseChartOptions: ChartOptions<T>) {
        const { activeTheme, defaultAxes, processedOptions: baseOptions } = baseChartOptions;

        const { presetType } = this.optionMetadata;
        const processor = presetType ? PRESET_DATA_PROCESSORS[presetType] : undefined;
        if (presetType != null && deltaOptions?.data != null && processor != null) {
            // Handle preset data transforms gracefully.
            const { series, data } = processor(deltaOptions.data);
            deltaOptions = mergeDefaults({ series, data }, deltaOptions) as DeepPartial<T>;
        }

        this.fastSeriesSetup(deltaOptions, baseOptions);
        const processedOptions = mergeDefaults(deltaOptions, baseOptions);

        ChartOptions.debug('ChartOptions.fastSetup() - processed options', processedOptions);

        return { activeTheme, defaultAxes, processedOptions, fastDelta: deltaOptions };
    }

    private fastSeriesSetup(deltaOptions: DeepPartial<T> | null, baseOptions: T) {
        if (!deltaOptions?.series) return;

        if (deltaOptions.series?.every((s, i) => jsonPropertyCompare(s, baseOptions.series?.[i] ?? {}))) {
            // No series changes - skip these.
            delete deltaOptions['series'];
        } else {
            // Need to take full series options in update cases.
            deltaOptions.series = deltaOptions.series.map((s, i) => {
                return merge(s, baseOptions.series?.[i] ?? {});
            });
        }
    }

    private slowSetup(processedOverrides: Partial<T>, deltaOptions?: DeepPartial<T> | null, stripSymbols = false) {
        let options = deepClone(this.userOptions, ChartOptions.OPTIONS_CLONE_OPTS) as T;

        if (deltaOptions) {
            options = mergeDefaults(deltaOptions, options) as T;
            if (stripSymbols) {
                this.removeLeftoverSymbols(options);
            }
        }

        const { presetType } = this.optionMetadata;
        if (presetType != null) {
            type PresetConstructor = (
                options: AgPresetOptions,
                themeOptions: AgPresetOptions | undefined,
                activeTheme: () => ChartTheme
            ) => T;

            const presetConstructor: PresetConstructor | undefined = (PRESETS as any)[presetType];

            const presetParams = options as any as AgPresetOptions;

            // Note financial charts defines the theme in its returned options,
            // so we need to get the theme before and after applying the preset
            const presetSubType = (options as any).type as keyof AgPresetOverrides | undefined;
            const presetTheme = presetSubType != null ? getChartTheme(options.theme).presets[presetSubType] : undefined;

            ChartOptions.debug('>>> AgCharts.createOrUpdate() - applying preset', presetParams);
            options = presetConstructor?.(presetParams, presetTheme, () => this.activeTheme) ?? options;
        }

        this.soloSeriesIntegrity(options);

        if (!enterpriseModule.isEnterprise) {
            removeUsedEnterpriseOptions(options);
        }

        const activeTheme = getChartTheme(options.theme);

        if (presetType != null) {
            activeTheme.templateTheme(options, false);
        }

        // Must run before chart validation to cleanup invalid types.
        this.validateSeriesOptions(options);

        this.chartDef = ModuleRegistry.detectChartDefinition(options);

        if (!this.chartDef.placeholder) {
            const { valid, errors } = validate(options, this.chartDef.options);
            errors.forEach((error) => Logger.warn(error));
            options = valid as T;
        }

        this.validatePluginOptions(options);
        this.validateAxesOptions(options);
        this.removeDisabledOptions(options);

        const seriesType = this.optionsType(options);
        const {
            annotations = {},
            axes: axesThemes = {},
            series: seriesTheme,
            ...themeDefaults
        } = this.getSeriesThemeConfig(seriesType, activeTheme);

        const [annotationsOptions, annotationsThemes] = this.splitAnnotationsOptions(annotations);
        const annotationThemes = deepClone(annotationsThemes);

        const defaultAxes = this.getDefaultAxes(options, seriesTheme);
        let processedOptions = mergeDefaults(
            processedOverrides,
            options,
            annotationsOptions,
            themeDefaults,
            defaultAxes
        );
        this.processAxesOptions(processedOptions, axesThemes);
        this.processSeriesOptions(processedOptions, activeTheme);

        // Create isolated copy of options before we start mutations - this is performance sensitive,
        // so we aim to only do this once in the processing flow.
        processedOptions = deepClone(processedOptions, ChartOptions.OPTIONS_CLONE_OPTS);

        // Disable legend by default for single series cartesian charts and polar charts which display legend items per series rather than data items
        if (
            (isAgCartesianChartOptions(processedOptions) ||
                isAgStandaloneChartOptions(processedOptions) ||
                isAgPolarChartOptionsWithSeriesBasedLegend(processedOptions)) &&
            processedOptions.legend?.enabled == null
        ) {
            processedOptions.legend ??= {};
            processedOptions.legend.enabled = processedOptions.series!.length > 1;
        }

        this.enableConfiguredOptions(processedOptions, options);

        const themeParameters = this.getThemeParameters(activeTheme, processedOptions) as AgChartThemeParams;
        (themeParameters as any).__palette = deepClone(activeTheme.palette);
        (themeParameters as any).__palette.type = isObject(options.theme)
            ? paletteType(options.theme?.palette)
            : 'inbuilt';
        this.resolveThemeOperations(themeParameters, themeParameters);
        this.resolveThemeOperations(themeParameters, processedOptions);
        this.resolveThemeOperations(themeParameters, annotationThemes);

        this.processMiniChartSeriesOptions(processedOptions);

        activeTheme.templateTheme(processedOptions, false);

        removeUnusedEnterpriseOptions(processedOptions);
        if (!enterpriseModule.isEnterprise) {
            removeUsedEnterpriseOptions(processedOptions, true);
        }

        this.validateSeriesOptions(processedOptions);
        this.validateAxesOptions(processedOptions);

        ChartOptions.debug(() => ['ChartOptions.slowSetup() - processed options', deepClone(processedOptions)]);

        return { activeTheme, processedOptions, defaultAxes, themeParameters, annotationThemes };
    }

    private validatePluginOptions(options: T) {
        for (const pluginDef of ModuleRegistry.listModulesByType(ModuleType.Plugin)) {
            const pluginKey = pluginDef.name as keyof T;
            if (pluginKey in options && (!pluginDef.chartType || pluginDef.chartType === this.chartDef?.name)) {
                const { valid, errors } = validate(options[pluginKey], pluginDef.options, pluginDef.name);
                errors.forEach((error) => Logger.warn(error));
                options[pluginKey] = valid as T[keyof T];
            }
        }
    }

    private validateSeriesOptions(options: T): void {
        const chartType = this.chartDef?.name;
        const validatedSeriesOptions: any[] = [];
        const seriesCount = options.series?.length ?? 0;

        let validSeriesTypes: string | undefined;
        for (let index = 0; index < seriesCount; index++) {
            const keyPath = `series[${index}]`;
            const seriesOptions = options.series![index];
            const seriesDef = ModuleRegistry.getSeriesModule(seriesOptions.type ?? 'line');

            if (seriesDef == null) {
                validSeriesTypes ??= joinFormatted(
                    Array.from(ModuleRegistry.listModulesByType(ModuleType.Series))
                        .filter((def) => !chartType || def.chartType === chartType)
                        .map((def) => def.name),
                    'or',
                    stringFormat
                );
                Logger.warn(
                    `Unknown type \`${seriesOptions.type}\` at \`${keyPath}.type\`; expecting ${validSeriesTypes}, ignoring.`
                );
                continue;
            } else if (chartType && seriesDef.chartType !== chartType) {
                Logger.warn(
                    `Series type \`${seriesDef.name}\` at \`${keyPath}.type\` is not supported by chart type \`${chartType}\`, ignoring.`
                );
                continue;
            }

            const { validate: validateSeries = validate } = seriesDef;
            const { valid, errors } = validateSeries(seriesOptions, seriesDef.options, keyPath);

            errors.forEach((error) => Logger.warn(error));

            if (!errors.some((e) => e.required && e.path === keyPath)) {
                validatedSeriesOptions.push(valid);
            }
        }
        options.series = validatedSeriesOptions;
    }

    private validateAxesOptions(options: T) {
        if (!('axes' in options) || !options.axes) return;

        const chartType = this.chartDef?.name;
        const validatedAxesOptions: any[] = [];
        const axesCount = options.axes.length ?? 0;
        let validAxesTypes: string | undefined;
        for (let index = 0; index < axesCount; index++) {
            const keyPath = `axes[${index}]`;
            const axisOptions = options.axes[index];
            const axisDef = ModuleRegistry.getAxisModule(axisOptions.type);

            if (axisDef == null) {
                validAxesTypes ??= joinFormatted(
                    Array.from(ModuleRegistry.listModulesByType(ModuleType.Axis))
                        .filter((def) => def.chartType === chartType)
                        .map((def) => def.name),
                    'or',
                    stringFormat
                );
                Logger.warn(
                    `Unknown type \`${axisOptions.type}\` at  \`${keyPath}.type\`; expecting one of ${validAxesTypes}, ignoring all axes options.`
                );
                delete options.axes;
                break;
            } else if (axisDef.chartType !== chartType) {
                Logger.warn(
                    `Axis type \`${axisDef.name}\` at  \`${keyPath}.type\` is not supported by chart type \`${chartType}\`, ignoring.`
                );
                break;
            }

            const { validate: validateAxis = validate } = axisDef;
            const { valid, errors } = validateAxis(axisOptions, axisDef.options, keyPath);

            errors.forEach((error) => Logger.warn(error));

            if (!errors.some((e) => e.required && e.path === keyPath)) {
                validatedAxesOptions.push(valid);
            }
        }
        options.axes = validatedAxesOptions;
    }

    diffOptions(other?: ChartOptions): Partial<T> {
        // Detect first creation case.
        if (this === other) return {};
        if (other == null) return this.processedOptions;

        return (
            (this.fastDelta as Partial<T>) ??
            jsonDiff(other.processedOptions, this.processedOptions, ChartOptions.JSON_DIFF_OPTS)
        );
    }

    private getSeriesThemeConfig(seriesType: string, activeTheme: ChartTheme) {
        return activeTheme?.config[seriesType] ?? {};
    }

    private getDefaultAxes(options: T, seriesTheme: object) {
        const optionsType = this.optionsType(options);
        const firstSeriesOptions = options.series?.find((series) => (series.type ?? 'line') === optionsType) ?? {};
        const axes = seriesRegistry.cloneDefaultAxes(optionsType) as T;
        jsonResolveOperations(axes, {}, undefined, mergeDefaults(firstSeriesOptions, seriesTheme));
        return axes;
    }

    private optionsType(options: Partial<T>) {
        return options.series?.[0]?.type ?? 'line';
    }

    private splitAnnotationsOptions(annotations: any) {
        const {
            axesButtons = null,
            enabled = null,
            optionsToolbar = null,
            toolbar = null,
            ...annotationsThemes
        } = annotations;

        if (axesButtons == null && enabled == null && optionsToolbar == null && toolbar == null) {
            return [{}, annotationsThemes];
        }

        return [{ annotations: { axesButtons, enabled, optionsToolbar, toolbar } }, annotationsThemes];
    }

    private processAxesOptions(options: T, axesThemes: any) {
        if (!('axes' in options)) return;
        options.axes = options.axes?.map((axis: any) => {
            const { crossLines: crossLinesTheme, ...axisTheme } = mergeDefaults(
                axesThemes[axis.type]?.[axis.position],
                axesThemes[axis.type]
            );

            if (axis.crossLines) {
                axis.crossLines = mergeArrayDefaults(axis.crossLines, crossLinesTheme);
            }

            const gridLineStyle = axisTheme.gridLine?.style;
            if (axis.gridLine?.style && gridLineStyle?.length) {
                axis.gridLine.style = axis.gridLine.style.map((style: any, index: number) =>
                    style.stroke != null || style.lineDash != null
                        ? mergeDefaults(style, gridLineStyle.at(index % gridLineStyle.length))
                        : style
                );
            }
            const { top: _1, right: _2, bottom: _3, left: _4, ...axisOptions } = mergeDefaults(axis, axisTheme);
            return axisOptions;
        }) as AgCartesianAxisOptions[] | AgPolarAxisOptions[];
    }

    private processSeriesOptions(options: T, activeTheme: ChartTheme) {
        const defaultTooltipPosition = this.getTooltipPositionDefaults(options);

        const processedSeries = (options.series as SeriesOptionsTypes[])?.map((series) => {
            series.type ??= 'line'; // TODO remove this behaviour
            const { innerLabels: innerLabelsTheme, ...seriesTheme } =
                this.getSeriesThemeConfig(series.type, activeTheme).series ?? {};

            const seriesDef = ModuleRegistry.getSeriesModule(series.type);
            const tooltipDefined = Boolean(seriesDef?.options.tooltip);
            const visibleDefined = Boolean(seriesDef?.options.visible);
            const defaultTooltipRange = tooltipDefined && this.getTooltipRangeDefaults(options, series.type);

            const seriesOptions = mergeDefaults(
                this.getSeriesGroupingOptions(series),
                series,
                tooltipDefined && defaultTooltipPosition,
                defaultTooltipRange,
                seriesTheme,
                visibleDefined && { visible: true }
            );

            if (seriesOptions.innerLabels) {
                seriesOptions.innerLabels = mergeArrayDefaults(seriesOptions.innerLabels, innerLabelsTheme);
            }

            return seriesOptions;
        });

        options.series = this.setSeriesGroupingOptions(processedSeries ?? []);
    }

    private processMiniChartSeriesOptions(options: T) {
        let miniChartSeries = options.navigator?.miniChart?.series;
        if (miniChartSeries == null) return;

        miniChartSeries = miniChartSeries.map((series) => {
            series.type ??= 'line';
            return series;
        });

        options.navigator!.miniChart!.series = this.setSeriesGroupingOptions(miniChartSeries) as any;
    }

    private getThemeParameters(theme: ChartTheme, options: T) {
        const defaultParameters = theme.getPublicParameters();

        if (!isPlainObject(options.theme) || !options.theme.params) {
            return defaultParameters;
        }

        const { valid, errors } = validate(options.theme.params, themeOptionsDef.params);

        for (const { message } of errors) {
            Logger.warnOnce(message);
        }

        return mergeDefaults(valid, defaultParameters);
    }

    private resolveThemeOperations(params: WithThemeParams<AgChartThemeParams>, options: object) {
        const modifiedPaths = jsonResolveOperations(options, params, new Set(['palette', 'data', 'theme']));
        ChartOptions.debug('ChartOptions.resolveTheme()', modifiedPaths);
    }

    private getSeriesGroupingOptions(series: SeriesOptionsTypes & GroupingOptions) {
        const groupable = seriesRegistry.isGroupable(series.type);
        const stackable = seriesRegistry.isStackable(series.type);
        const stackedByDefault = seriesRegistry.isStackedByDefault(series.type);

        if (series.grouped && !groupable) {
            Logger.warnOnce(`unsupported grouping of series type "${series.type}".`);
        }
        if ((series.stacked || series.stackGroup) && !stackable) {
            Logger.warnOnce(`unsupported stacking of series type "${series.type}".`);
        }

        let { grouped, stacked } = series;

        stacked ??= (stackedByDefault || series.stackGroup != null) && !(groupable && grouped);
        grouped ??= true;

        return {
            stacked: stackable && stacked,
            grouped: groupable && grouped && !(stackable && stacked),
        };
    }

    private setSeriesGroupingOptions(allSeries: GroupingSeriesOptions[]) {
        const seriesGroups = this.getSeriesGrouping(allSeries);

        ChartOptions.debug('ChartOptions.setSeriesGroupingOptions() - series grouping: ', seriesGroups);

        const groupIdx: Record<string, number> = {};
        const groupCount = seriesGroups.reduce<Record<string, number>>((countMap, seriesGroup) => {
            if (seriesGroup.groupType === GroupingType.DEFAULT) {
                return countMap;
            }
            countMap[seriesGroup.seriesType] ??= 0;
            countMap[seriesGroup.seriesType] +=
                seriesGroup.groupType === GroupingType.STACK ? 1 : seriesGroup.series.length;
            return countMap;
        }, {});

        // sort series by grouping and enrich with seriesGrouping metadata
        return seriesGroups
            .flatMap((seriesGroup) => {
                groupIdx[seriesGroup.seriesType] ??= 0;
                switch (seriesGroup.groupType) {
                    case GroupingType.STACK: {
                        const groupIndex = groupIdx[seriesGroup.seriesType]++;
                        return seriesGroup.series.map((series, stackIndex) =>
                            Object.assign(series, {
                                seriesGrouping: {
                                    groupId: seriesGroup.groupId,
                                    groupIndex,
                                    groupCount: groupCount[seriesGroup.seriesType],
                                    stackIndex,
                                    stackCount: seriesGroup.series.length,
                                },
                            })
                        );
                    }

                    case GroupingType.GROUP:
                        return seriesGroup.series.map((series) =>
                            Object.assign(series, {
                                seriesGrouping: {
                                    groupId: seriesGroup.groupId,
                                    groupIndex: groupIdx[seriesGroup.seriesType]++,
                                    groupCount: groupCount[seriesGroup.seriesType],
                                    stackIndex: 0,
                                    stackCount: 0,
                                },
                            })
                        );
                }

                return seriesGroup.series;
            })
            .map(({ stacked: _, grouped: __, ...seriesOptions }) => seriesOptions) as T['series'];
    }

    private getSeriesGroupId(series: GroupingSeriesOptions) {
        return [series.type, series.xKey, series.stacked ? series.stackGroup ?? 'stacked' : 'grouped']
            .filter(Boolean)
            .join('-');
    }

    private getSeriesGrouping(allSeries: GroupingSeriesOptions[]) {
        const groupMap = new Map<string, SeriesGroup>();
        return allSeries.reduce<SeriesGroup[]>((result, series) => {
            const seriesType = series.type!;
            if (!series.stacked && !series.grouped) {
                result.push({ groupType: GroupingType.DEFAULT, seriesType, series: [series], groupId: '__default__' });
            } else {
                const groupId = this.getSeriesGroupId(series);
                if (!groupMap.has(groupId)) {
                    const groupType = series.stacked ? GroupingType.STACK : GroupingType.GROUP;
                    const record = { groupType, seriesType, series: [], groupId };
                    groupMap.set(groupId, record);
                    result.push(record);
                }
                groupMap.get(groupId)!.series.push(series);
            }
            return result;
        }, []);
    }

    private getTooltipPositionDefaults(options: T) {
        const position = options.tooltip?.position;
        if (!isPlainObject(position)) {
            return;
        }

        // eslint-disable-next-line sonarjs/deprecation
        const { type, anchorTo, placement, xOffset, yOffset } = position;
        const result: AgTooltipPositionOptions = {};

        // eslint-disable-next-line sonarjs/deprecation
        if (isString(type) && isEnumValue(AgTooltipPositionType, type)) {
            // eslint-disable-next-line sonarjs/deprecation
            result.type = type;
        }
        if (isString(anchorTo) && isEnumValue(AgTooltipAnchorToType, anchorTo)) {
            result.anchorTo = anchorTo;
        }
        if (isString(placement) && isEnumValue(AgTooltipPlacementType, placement)) {
            result.placement = placement;
        }
        if (isFiniteNumber(xOffset)) {
            result.xOffset = xOffset;
        }
        if (isFiniteNumber(yOffset)) {
            result.yOffset = yOffset;
        }
        return { tooltip: { position: result } };
    }

    // AG-11591 Support for new series-specific & legacy chart-global 'tooltip.range' options
    //
    // The `chart.series[].tooltip.range` option is a bit different for legacy reason. This use to be
    // global option (`chart.tooltip.range`) that could override the theme. But now, the tooltip range
    // option is series-specific.
    //
    // To preserve backward compatibility, the `chart.tooltip.range` theme default has been changed from
    // 'nearest' to undefined.
    private getTooltipRangeDefaults(options: T, seriesType: SeriesType) {
        return {
            tooltip: {
                range: options.tooltip?.range ?? seriesRegistry.getTooltipDefauls(seriesType)?.range,
            },
        };
    }

    private soloSeriesIntegrity(options: Partial<T>) {
        if (!isArray(options.series as unknown)) return;
        const allSeries: SeriesOptionsTypes[] | undefined = options.series;
        if (allSeries && allSeries.length > 1 && allSeries.some((series) => seriesRegistry.isSolo(series.type))) {
            const mainSeriesType = this.optionsType(options);
            if (seriesRegistry.isSolo(mainSeriesType)) {
                Logger.warn(
                    `series[0] of type '${mainSeriesType}' is incompatible with other series types. Only processing series[0]`
                );
                options.series = allSeries.slice(0, 1) as T['series'];
            } else {
                const { solo, nonSolo } = groupBy(allSeries, (s) =>
                    seriesRegistry.isSolo(s.type) ? 'solo' : 'nonSolo'
                );
                const rejects = unique(solo!.map((s) => s.type)).join(', ');
                Logger.warn(`Unable to mix these series types with the lead series type: ${rejects}`);
                options.series = nonSolo as T['series'];
            }
        }
    }

    private static enableConfiguredJsonOptions(this: void, visitingUserOpts: any, visitingMergedOpts: any) {
        if (
            typeof visitingMergedOpts === 'object' &&
            'enabled' in visitingMergedOpts &&
            !visitingMergedOpts._enabledFromTheme &&
            visitingUserOpts.enabled == null
        ) {
            visitingMergedOpts.enabled = true;
        }
    }

    private static cleanupEnabledFromThemeJsonOptions(this: void, visitingMergedOpts: any) {
        if (visitingMergedOpts._enabledFromTheme != null) {
            // Do not apply special handling, base enablement on theme.
            delete visitingMergedOpts._enabledFromTheme;
        }
    }

    private enableConfiguredOptions(options: T, userOptions: T) {
        // Set `enabled: true` for all option objects where the user has provided values.
        jsonWalk(userOptions, ChartOptions.enableConfiguredJsonOptions, new Set(['data', 'theme']), options);

        // Cleanup any special properties.
        jsonWalk(options, ChartOptions.cleanupEnabledFromThemeJsonOptions, new Set(['data', 'theme']));
    }

    private static removeDisabledOptionJson(this: void, optionsNode: any) {
        if ('enabled' in optionsNode && optionsNode.enabled === false) {
            Object.keys(optionsNode).forEach((key) => {
                if (key === 'enabled') return;
                delete optionsNode[key];
            });
        }
    }

    private removeDisabledOptions(options: Partial<T>) {
        // Remove configurations from all option objects with a `false` value for the `enabled` property.
        jsonWalk(options, ChartOptions.removeDisabledOptionJson, new Set(['data', 'theme']));
    }

    private static removeLeftoverSymbolsJson(this: void, optionsNode: any) {
        if (!optionsNode || !isObject(optionsNode)) return;
        for (const key of Object.keys(optionsNode)) {
            const value = optionsNode[key];
            if (isSymbol(value)) {
                delete optionsNode[key];
            }
        }
    }

    private removeLeftoverSymbols(options: Partial<T>) {
        jsonWalk(options, ChartOptions.removeLeftoverSymbolsJson, new Set(['data']));
    }

    private specialOverridesDefaults(options: Partial<ChartSpecialOverrides>) {
        if (options.window == null) {
            options.window = getWindow();
        } else {
            setWindow(options.window);
        }

        if (options.document == null) {
            options.document = getDocument();
        } else {
            setDocument(options.document);
        }

        if (options.window == null) {
            throw new Error('AG Charts - unable to resolve global window');
        }
        if (options.document == null) {
            throw new Error('AG Charts - unable to resolve global document');
        }

        return options as ChartSpecialOverrides;
    }
}
